import { ArrowLeft, KeyRound, LoaderCircle } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { confirmPasswordReset, extractErrorMessage, verifyEmailLink } from "../lib/api";
import { LumenBrand } from "../components/common/LumenBrand";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [formState, setFormState] = useState({ password: "", confirmPassword: "" });
  const [saving, setSaving] = useState(false);
  const [verifiedAccessToken, setVerifiedAccessToken] = useState("");
  const [verifyingLink, setVerifyingLink] = useState(false);
  const [linkError, setLinkError] = useState("");

  const resetContext = useMemo(() => {
    const hash = window.location.hash.startsWith("#") ? window.location.hash.slice(1) : "";
    const hashParams = new URLSearchParams(hash);
    const queryParams = new URLSearchParams(window.location.search);
    return {
      accessToken: hashParams.get("access_token") || "",
      tokenHash: queryParams.get("token_hash") || "",
      flowType: hashParams.get("type") || queryParams.get("type") || "recovery",
    };
  }, []);

  useEffect(() => {
    if (resetContext.accessToken || !resetContext.tokenHash) {
      return;
    }

    let active = true;
    const verifyLink = async () => {
      setVerifyingLink(true);
      setLinkError("");
      try {
        const payload = await verifyEmailLink({ token_hash: resetContext.tokenHash, type: resetContext.flowType });
        if (active) {
          setVerifiedAccessToken(payload.access_token || "");
        }
      } catch (error) {
        if (active) {
          setLinkError(extractErrorMessage(error, "This reset link is missing or expired."));
        }
      } finally {
        if (active) {
          setVerifyingLink(false);
        }
      }
    };

    verifyLink();
    return () => {
      active = false;
    };
  }, [resetContext.accessToken, resetContext.flowType, resetContext.tokenHash]);

  const resolvedAccessToken = resetContext.accessToken || verifiedAccessToken;

  const handleChange = (event) => {
    setFormState((current) => ({ ...current, [event.target.name]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (formState.password !== formState.confirmPassword) {
      toast.error("Passwords must match.");
      return;
    }
    setSaving(true);
    try {
      await confirmPasswordReset({ access_token: resolvedAccessToken, password: formState.password });
      toast.success(resetContext.flowType === "invite" ? "Password created. You can sign in now." : "Password updated. You can sign in now.");
      window.history.replaceState({}, document.title, "/reset-password");
      navigate("/login", { replace: true });
    } catch (error) {
      toast.error(extractErrorMessage(error, "Unable to update your password."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[radial-gradient(circle_at_top_left,_rgba(253,230,138,0.32),transparent_20%),radial-gradient(circle_at_bottom_right,_rgba(125,211,252,0.24),transparent_20%),linear-gradient(135deg,#f8f4eb_0%,#fbfcff_50%,#eef3f9_100%)] px-4 py-8" data-testid="reset-password-page">
      <div className="panel-surface w-full max-w-xl rounded-[2rem] px-6 py-7 sm:px-8">
        <div className="mb-6"><LumenBrand testId="reset-password-brand" /></div>
        <Link className="inline-flex items-center gap-2 text-sm font-medium text-zinc-500 transition hover:text-zinc-900" data-testid="reset-password-back-link" to="/login"><ArrowLeft className="h-4 w-4" /> Back to sign in</Link>

        <div className="mt-6 flex items-start gap-4">
          <div className="rounded-2xl bg-zinc-100 p-3 text-zinc-700"><KeyRound className="h-5 w-5" /></div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Secure password setup</p>
            <h1 className="mt-2 text-4xl font-semibold text-zinc-900" data-testid="reset-password-title">{resetContext.flowType === "invite" ? "Set your password" : "Choose a new password"}</h1>
          </div>
        </div>

        {verifyingLink ? (
          <div className="mt-8 flex items-center gap-3 rounded-[28px] border border-zinc-200 bg-zinc-50 px-5 py-5" data-testid="reset-password-verifying-card">
            <LoaderCircle className="h-5 w-5 animate-spin text-zinc-600" />
            <div>
              <p className="text-sm font-semibold text-zinc-900">Verifying your secure link</p>
            </div>
          </div>
        ) : resolvedAccessToken ? (
          <form className="mt-8 space-y-4" data-testid="reset-password-form" onSubmit={handleSubmit}>
            <label className="block space-y-2">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">New password</span>
              <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="reset-password-input" minLength={8} name="password" onChange={handleChange} required type="password" value={formState.password} />
            </label>
            <label className="block space-y-2">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-zinc-500">Confirm password</span>
              <input className="w-full rounded-2xl border border-zinc-200 bg-white px-4 py-3 text-sm" data-testid="reset-password-confirm-input" minLength={8} name="confirmPassword" onChange={handleChange} required type="password" value={formState.confirmPassword} />
            </label>
            <button className="inline-flex items-center gap-2 rounded-2xl bg-zinc-900 px-5 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70" data-testid="reset-password-submit-button" disabled={saving} type="submit">{saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}{saving ? "Saving..." : "Save password"}</button>
          </form>
        ) : (
          <div className="mt-8 rounded-[28px] border border-amber-200 bg-amber-50 px-5 py-5" data-testid="reset-password-invalid-link-card">
            <p className="text-sm font-semibold text-amber-800">{linkError || "This reset link is missing or expired."}</p>
          </div>
        )}
      </div>
    </div>
  );
}